import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Heart,
  BookOpen,
  Moon,
  Sun,
  Path,
  CaretRight,
  Trophy,
  Ticket,
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';
import { useCollections } from '../context/CollectionsContext';
import { useTheme } from '../context/ThemeContext';
import ContactFooter from '../components/ContactFooter';
import telegramService from '../services/telegram';
import { getMyGiveaways } from '../services/api';
import { formatMonthYear } from '../utils/monthYear';

// Строка списка: одинаковый каркас у сводки и у настроек, чтобы два
// блока на экране не выглядели разными видами списка
const ROW_CLASS =
  'w-full flex items-center gap-3 px-4 h-14 text-left ' +
  'transition-colors duration-fast ease-out active:bg-theme-card-secondary';

const SectionTitle = ({ children }) => (
  <h2 className="text-h2 text-theme-text-primary mb-3">{children}</h2>
);

// Список с общей рамкой и разделителями между строками
const RowGroup = ({ children }) => (
  <div
    className="overflow-hidden"
    style={{
      background: 'var(--color-card)',
      // В светлой теме card и bg оба белые — без границы блок исчезает.
      // Брендбук 2.8
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-lg)',
    }}
  >
    {children}
  </div>
);

const RowDivider = () => (
  <div style={{ height: 1, background: 'var(--color-border)', marginLeft: 52 }} />
);

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { favorites } = useFavorites();
  // Числа берутся из контекста, а не грузятся здесь: тогда они меняются
  // сразу после сохранения или удаления на другом экране — как у мест
  const { routesCount, collectionsCount } = useCollections();
  const { toggleTheme, isDark } = useTheme();

  const [giveaways, setGiveaways] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) return;
    getMyGiveaways()
      .then(r => setGiveaways(r.data || []))
      .catch(() => {});
  }, [isAuthenticated]);

  const getInitials = () => {
    if (!user) return '?';
    const firstName = user.firstName || user.first_name || '';
    const lastName = user.lastName || user.last_name || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';
  };

  const getFullName = () => {
    if (!user) return 'Пользователь';
    const firstName = user.firstName || user.first_name || '';
    const lastName = user.lastName || user.last_name || '';
    return `${firstName} ${lastName}`.trim() || user.username || 'Пользователь';
  };

  const getUsername = () => {
    if (!user) return '';
    return user.telegramUsername || user.username || '';
  };

  const getPhotoUrl = () => {
    if (!user) return null;
    return user.photoUrl || user.photo_url || null;
  };

  // Строкой, а не чипом: месяц и год — точная дата тут ничего не решает.
  // Падеж родительный («с декабря»), Intl без дня даёт именительный
  const getRegistrationDate = () => {
    if (!user || !user.registeredAt) return null;
    return formatMonthYear(user.registeredAt);
  };

  const goToFavorites = (tab) => {
    telegramService.hapticFeedback('light');
    navigate(`/favorites?tab=${tab}`);
  };

  const summary = [
    { id: 'places', icon: Heart, label: 'Места', count: favorites.length },
    { id: 'routes', icon: Path, label: 'Маршруты', count: routesCount },
    { id: 'collections', icon: BookOpen, label: 'Подборки', count: collectionsCount },
  ];

  // Победа выше активного участия и остаётся после завершения розыгрыша
  const sortedGiveaways = [...giveaways].sort((a, b) => {
    if (a.isWinner !== b.isWinner) return a.isWinner ? -1 : 1;
    return new Date(a.endDate) - new Date(b.endDate);
  });

  const now = Date.now();
  const visibleGiveaways = sortedGiveaways.filter(
    g => g.isWinner || (g.isActive !== false && new Date(g.endDate).getTime() > now)
  );

  const formatEndDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
  };

  return (
    <div className="min-h-screen bg-theme-bg pb-6">
      <section
        className="px-4 pb-4"
        style={{ paddingTop: 'calc(var(--safe-area-top) + var(--tg-header-height) + 16px)' }}
      >
        <h1 className="text-h2 text-theme-text-primary">Профиль</h1>
      </section>

      {/* Кто пользователь */}
      <section className="px-4">
        <div
          className="flex items-center gap-4 p-4"
          style={{
            background: 'var(--color-card)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
          }}
        >
          <div className="flex-shrink-0">
            {getPhotoUrl() ? (
              <img
                src={getPhotoUrl()}
                alt=""
                className="block object-cover"
                style={{ width: 64, height: 64, borderRadius: 'var(--radius-full)' }}
              />
            ) : (
              <div
                className="flex items-center justify-center"
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 'var(--radius-full)',
                  background: 'var(--color-card-secondary)',
                  border: '1px solid var(--color-border)',
                }}
              >
                {getInitials() !== '?' ? (
                  <span className="text-h3 text-theme-text-secondary">{getInitials()}</span>
                ) : (
                  <User size={24} weight="light" className="text-theme-text-hint" />
                )}
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="text-h3 text-theme-text-primary truncate">{getFullName()}</div>
            {getUsername() && (
              <div className="text-caption text-theme-text-secondary truncate mt-0.5">
                @{getUsername()}
              </div>
            )}
            {getRegistrationDate() && (
              <div className="text-caption text-theme-text-hint mt-1">
                В приложении с {getRegistrationDate()}
              </div>
            )}
          </div>
        </div>

        {!isAuthenticated && (
          <p className="text-body-sm text-theme-text-secondary mt-3">
            Откройте приложение в Telegram, чтобы сохранять места и участвовать
            в розыгрышах.
          </p>
        )}
      </section>

      {/* Сохранения — числом и одним тапом, ленты на экране нет */}
      {isAuthenticated && (
        <section className="px-4 mt-8">
          <SectionTitle>Мои сохранения</SectionTitle>
          <RowGroup>
            {summary.map((row, i) => {
              const Icon = row.icon;
              return (
                <div key={row.id}>
                  {i > 0 && <RowDivider />}
                  <button className={ROW_CLASS} onClick={() => goToFavorites(row.id)}>
                    <Icon size={20} className="text-theme-text-hint flex-shrink-0" />
                    <span className="flex-1 text-body text-theme-text-primary">{row.label}</span>
                    <span className="text-body text-theme-text-secondary tabular-nums">
                      {row.count}
                    </span>
                    <CaretRight size={16} className="text-theme-text-hint flex-shrink-0" />
                  </button>
                </div>
              );
            })}
          </RowGroup>
        </section>
      )}

      {/* Розыгрыши — участие и победа. Нет ни того ни другого, блока нет */}
      {isAuthenticated && visibleGiveaways.length > 0 && (
        <section className="px-4 mt-8">
          <SectionTitle>Розыгрыши</SectionTitle>
          <div className="flex flex-col gap-2">
            {visibleGiveaways.map(g => {
              const endLabel = formatEndDate(g.endDate);
              return (
                <button
                  key={g.id}
                  onClick={() => {
                    telegramService.hapticFeedback('light');
                    navigate(`/giveaway/${g.slug}`);
                  }}
                  className="w-full flex items-center gap-3 p-4 text-left transition-colors duration-fast ease-out active:opacity-80"
                  style={{
                    // Зелёный только у победы: достигнутый факт, а не призыв.
                    // Брендбук 2.1
                    // Не card: в светлой теме он равен фону, и без обводки
                    // строка исчезла бы (брендбук 2.8). Подложка вместо рамки
                    background: g.isWinner ? 'var(--color-success-dim)' : 'var(--color-card-secondary)',
                    border: 'none',
                    borderRadius: 'var(--radius-lg)',
                  }}
                >
                  {g.isWinner ? (
                    <Trophy size={20} weight="fill" style={{ color: 'var(--color-success)' }} className="flex-shrink-0" />
                  ) : (
                    <Ticket size={20} className="text-theme-text-hint flex-shrink-0" />
                  )}
                  <span className="flex-1 min-w-0">
                    <span className="block text-body text-theme-text-primary truncate">
                      {g.isWinner ? `Вы выиграли — «${g.title}»` : g.title}
                    </span>
                    <span className="block text-caption text-theme-text-secondary truncate mt-0.5">
                      {g.isWinner
                        ? endLabel
                          ? `Итоги подведены ${endLabel}`
                          : 'Итоги подведены'
                        : endLabel
                          ? `Участвуете · итоги ${endLabel}`
                          : 'Участвуете'}
                    </span>
                  </span>
                  <CaretRight size={16} className="text-theme-text-hint flex-shrink-0" />
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Настройки — внизу, где их и ищут */}
      <section className="px-4 mt-8">
        <SectionTitle>Настройки</SectionTitle>
        <RowGroup>
          {/* Тумблер, а не строка со значением: справа стояло «Светлая»,
              и это читалось как показание, а не как переключатель.
              Подпись называет текущую тему и идёт заодно с иконкой:
              солнце + «Светлая тема», месяц + «Тёмная тема».
              role="switch" — состояние озвучивается скринридером */}
          <button
            className={ROW_CLASS}
            role="switch"
            aria-checked={isDark}
            aria-label="Тёмная тема"
            onClick={() => {
              telegramService.hapticFeedback('light');
              toggleTheme();
            }}
          >
            {isDark ? (
              <Moon size={20} weight="fill" className="text-theme-text-hint flex-shrink-0" />
            ) : (
              <Sun size={20} weight="fill" className="text-theme-text-hint flex-shrink-0" />
            )}
            <span className="flex-1 text-body text-theme-text-primary">
              {isDark ? 'Тёмная тема' : 'Светлая тема'}
            </span>
            <span
              className="relative flex-shrink-0 transition-colors duration-base ease-out"
              style={{
                width: 48,
                height: 28,
                borderRadius: 'var(--radius-full)',
                background: isDark ? 'var(--color-action)' : 'var(--color-card-secondary)',
                // Выключенный тумблер на карточке — своя граница ≥3:1,
                // иначе серое на белом не видно вовсе. Брендбук 2.9
                border: `1px solid ${isDark ? 'var(--color-action)' : 'var(--color-border-strong)'}`,
              }}
            >
              <span
                className="absolute block transition-transform duration-base ease-out"
                style={{
                  width: 22,
                  height: 22,
                  top: 2,
                  left: 2,
                  borderRadius: 'var(--radius-full)',
                  // Тени нет (брендбук 10.3): бегунок отделяется цветом.
                  // На синем — белый, на серой подложке — цвет карточки
                  background: isDark ? 'var(--color-on-action)' : 'var(--color-card)',
                  border: isDark ? 'none' : '1px solid var(--color-border-strong)',
                  transform: isDark ? 'translateX(20px)' : 'translateX(0)',
                }}
              />
            </span>
          </button>
        </RowGroup>
      </section>

      <div className="px-4">
        <ContactFooter />
      </div>
    </div>
  );
};

export default ProfilePage;
